import React from "react";
import { User } from "lucide-react";

// Small shadcn-style building blocks (Tailwind only, no extra packages), in the app's zinc black-and-white theme.

export const cn = (...classes) => classes.filter(Boolean).join(" ");

const BUTTON_VARIANTS = {
  default: "bg-zinc-900 text-white hover:bg-zinc-800 border border-zinc-900",
  outline: "bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300",
  secondary: "bg-zinc-100 text-zinc-900 border border-transparent hover:bg-zinc-200",
  ghost: "bg-transparent text-zinc-600 border border-transparent hover:bg-zinc-100 hover:text-zinc-900",
  destructive: "bg-white text-red-600 border border-zinc-200 hover:bg-red-50 hover:border-red-200",
  warning: "bg-amber-500 text-white border border-amber-500 hover:bg-amber-600",
};
const BUTTON_SIZES = {
  sm: "h-8 px-3 text-xs gap-1.5",
  default: "h-9 px-4 text-sm gap-2",
  icon: "h-8 w-8 justify-center",
  "icon-sm": "h-7 w-7 justify-center",
};

export const Button = React.forwardRef(
  ({ variant = "default", size = "default", className, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        "inline-flex items-center rounded-md font-medium transition-colors focus-visible:outline-none",
        "focus-visible:ring-2 focus-visible:ring-zinc-900/20 disabled:pointer-events-none disabled:opacity-50 shrink-0",
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className
      )}
      {...props}
    />
  )
);
Button.displayName = "Button";

const BADGE_VARIANTS = {
  default: "bg-zinc-100 text-zinc-700 border-zinc-200",
  dark: "bg-zinc-900 text-white border-zinc-900",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  danger: "bg-red-50 text-red-700 border-red-200",
  info: "bg-sky-50 text-sky-700 border-sky-200",
  outline: "bg-white text-zinc-600 border-zinc-200",
};

export const Badge = ({ variant = "default", className, ...props }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium leading-none whitespace-nowrap",
      BADGE_VARIANTS[variant],
      className
    )}
    {...props}
  />
);

export const Separator = ({ className }) => <div className={cn("h-px w-full bg-zinc-200", className)} />;

// Initials avatar (no external image service: visitor ids must not leave the app)
const AVATAR_TONES = [
  "bg-zinc-100 text-zinc-700",
  "bg-sky-50 text-sky-700",
  "bg-violet-50 text-violet-700",
  "bg-emerald-50 text-emerald-700",
  "bg-amber-50 text-amber-700",
  "bg-rose-50 text-rose-700",
];

export const initials = (name) => {
  // letters only: a phone number or an id ("Visitor 6EAB78") gives no initials
  if (/^Visitor [0-9A-Z]{1,8}$/i.test(String(name || ""))) return "";
  const words = String(name || "").replace(/[^\p{L} ]/gu, " ").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export const Avatar = ({ name, seed, size = "md", className }) => {
  const key = String(seed || name || "");
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  const sizes = { sm: "h-7 w-7 text-[10px]", md: "h-9 w-9 text-xs", lg: "h-11 w-11 text-sm" };
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-semibold",
        AVATAR_TONES[h % AVATAR_TONES.length],
        sizes[size],
        className
      )}
      aria-hidden="true"
    >
      {initials(name) || <User className="h-[45%] w-[45%]" />}
    </div>
  );
};

export const Kbd = ({ children }) => (
  <kbd className="rounded border border-zinc-200 bg-zinc-50 px-1 font-sans text-[10px] font-medium text-zinc-500">
    {children}
  </kbd>
);
